<template>
  <div>
    <SupportSectionCurrentProduct
      class="sections-lateral-spaces"
      :name="product?.name || ''"
    />
    <div class="sections-lateral-spaces">

      <div class="page-content">

        <div class="flex flex-col lg:flex-row justify-between xl:w-9/12 mx-auto mt-16">
          <!-- Faqs -->
          <nv-collapse>
            <nv-collapse-item
              v-for="(faq, idx) in data"
              :key="faq.id"
            >
              <div slot="header" class="nv-d-flex nv-align-items-center nv-justify-content-between">
                <span class="nv-d-flex">
                  {{ faq.title }}
                </span>
                <nv-icon>expand-more</nv-icon>
              </div>
              <div slot="content" class="mobile-navigation-links nv-mb-2">
                <Markdown :content="faq.markdown || '{}'" :show-nav="false" :faq-mode="true"></Markdown>
              </div>
            </nv-collapse-item>
          </nv-collapse> 
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
// Packages

// Types
import { useProductStore } from '~/store/product';

definePageMeta({
  layout: 'support',
  middleware: 'check-product-slug'
});

// ---------------------------------------
// Hooks
// ---------------------------------------
const route = useRoute();
const { t, locale, localeProperties } = useI18n();
const localePath = useLocalePath();

// --------------------------------------
// Store
// --------------------------------------

const productStore = useProductStore();
const { product } = storeToRefs(productStore);

const {status, error, data } = await useFetch(`/api/products/${product.value?.slug}/faq?language_code=${localeProperties.value.language}`,
  {
    key: `supportFaq:${product.value?.slug}${locale.value}`,
    immediate: true,
  }
);

useSeoMeta({
  titleTemplate:  `%s | ${t('help_centre')}`,
  title: t("faq_product_title", { product_name: product.value?.name }),
  ogTitle: t("faq_product_title", { product_name: product.value?.name }),
  description: t("faq_seo_description", { product_name: product.value?.name }),
  ogDescription: t("faq_seo_description", { product_name: product.value?.name }),
})

</script>

<style lang="scss" scoped>
/*
* Mixes
*/
@import '@nuiverse/core/src/style/_utils';
@import '@nuiverse/core/src/style/mixins/_breakpoints';

.topics-list,
.articles-list {
  color: inherit;
  list-style: none;

  a {
    color: inherit;
  }
}

.topics-list {
  column-gap: 24px;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  row-gap: 24px;

  li {
    background: var(--nv-theme);
    border-radius: 8px;
    padding: 16px;

    &:hover {
      box-shadow: 0 0 4px 4px rgba(0, 99, 180, 0.12);
      cursor: pointer;
    }

    img {
      height: 40px;
      margin-right: 8px;
      width: 40px;
    }
  }
}

.articles-list {
  column-gap: 24px;
  grid-template-columns: repeat(auto-fill, minmax(248px, 1fr));
  row-gap: 24px;

  li {
    background: var(--nv-theme);
    border-radius: 8px;
    color: initial;
    height: 100%;
    padding: 24px;

    span {
      -webkit-box-orient: vertical;
      display: -webkit-box;
      -webkit-line-clamp: 3;
      overflow: hidden;
    }

    &:hover {
      box-shadow: 0 0 4px 4px rgba(0, 99, 180, 0.12);
      cursor: pointer;
    }

    img {
      height: 24px;
      margin-right: 8px;
      width: 24px;
    }
  }
}

.welcome-section {
  align-items: center;
  border-radius: 8px;
  display: flex;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 550px;
  overflow: hidden;
  position: relative;

  nv-search-box {
    max-width: 800px;
    width: 100%;
  }

  h2 {
    color: #fff;
    text-align: center;
    text-shadow: 2px 2px 4px #1f506e;
  }

  img {
    height: 104px;
    width: 104px;
  }

  .bg {
    background-image: url('https://assets.nuinalp.com/illustrations/account/sign-wallpaper-optimized.svg');
    background-position-y: 20%;
    background-repeat: no-repeat;
    background-size: cover;
    height: 100%;
    left: 0;
    pointer-events: none;
    position: absolute;
    top: 0;
    width: 100%;
    z-index: -1;

    &::before {
      background-color: rgba(31, 80, 110, 0.35);
      content: '';
      height: 100%;
      left: 0;
      pointer-events: none;
      position: absolute;
      top: 0;
      width: 100%;
    }
  }
}

</style>
